import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  canApproveForm,
  canSubmitInternalForm,
  fetchFormWorkflow,
  normalizeFormWorkflow,
} from '../lib/applicationFormWorkflow';
import { fetchMyAccountCreationCapabilities } from '../lib/accountCreationRoles';
import type { FormWorkflowConfig } from '../types/applicationForm';
import { getDbSupabase } from '../lib/supabase';

export function useFormWorkflowPermissions(formSlug = 'account-creation') {
  const { portalAccess } = useAuth();
  const [workflow, setWorkflow] = useState<FormWorkflowConfig | null>(null);
  const [departmentSlug, setDepartmentSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [canVerifyRpc, setCanVerifyRpc] = useState(false);
  const [canApproveRpc, setCanApproveRpc] = useState<boolean | null>(null);
  const [canSubmitRpc, setCanSubmitRpc] = useState<boolean | null>(null);

  const formsProfile = portalAccess?.forms.profile;
  const elevatedDepartment = portalAccess?.elevatedDepartment ?? null;
  const portalDepartmentSlug = portalAccess?.departmentSlug ?? null;
  const hasFormsAccess = Boolean(formsProfile?.role);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const [workflowData, formsDeptSlug] = await Promise.all([
          fetchFormWorkflow(formSlug),
          elevatedDepartment
            ? Promise.resolve(elevatedDepartment)
            : resolveFormsDepartmentSlug(formsProfile?.email),
        ]);
        if (!cancelled) {
          setWorkflow(workflowData);
          setDepartmentSlug(formsDeptSlug ?? portalDepartmentSlug);
        }

        if (formSlug === 'account-creation' && hasFormsAccess) {
          const caps = await fetchMyAccountCreationCapabilities();
          if (!cancelled) {
            setCanSubmitRpc(caps.canSubmit);
            setCanVerifyRpc(caps.canVerify);
            setCanApproveRpc(caps.canApprove);
          }
        } else if (!cancelled) {
          setCanSubmitRpc(null);
          setCanVerifyRpc(false);
          setCanApproveRpc(null);
        }
      } catch {
        if (!cancelled) {
          setWorkflow(normalizeFormWorkflow(null));
          setDepartmentSlug(elevatedDepartment ?? portalDepartmentSlug);
          setCanSubmitRpc(null);
          setCanVerifyRpc(false);
          setCanApproveRpc(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [formSlug, formsProfile?.email, elevatedDepartment, portalDepartmentSlug, hasFormsAccess]);

  const access = useMemo(
    () => ({
      role: formsProfile?.role ?? 'user',
      departmentSlug: elevatedDepartment ?? departmentSlug ?? portalDepartmentSlug,
    }),
    [formsProfile?.role, elevatedDepartment, departmentSlug, portalDepartmentSlug],
  );

  const canSubmitInternal = useMemo(() => {
    if (!workflow || !hasFormsAccess) return false;
    if (canSubmitRpc != null) return canSubmitRpc;
    return canSubmitInternalForm(access, workflow, formSlug);
  }, [workflow, hasFormsAccess, canSubmitRpc, access, formSlug]);

  const canApprove = useMemo(() => {
    if (!workflow || !hasFormsAccess) return false;
    if (canApproveRpc != null) return canApproveRpc;
    return canApproveForm(access, workflow);
  }, [workflow, hasFormsAccess, canApproveRpc, access]);

  return {
    workflow,
    loading,
    canApprove,
    canVerify: formSlug === 'account-creation' ? canVerifyRpc : false,
    canSubmitInternal,
    departmentSlug,
    formsRole: formsProfile?.role ?? null,
  };
}

async function resolveFormsDepartmentSlug(email?: string | null): Promise<string | null> {
  const trimmed = email?.trim();
  if (!trimmed) return null;

  try {
    const { supabase } = await import('../lib/supabase');
    if (supabase) {
      const { data: wide } = await supabase
        .schema('access_management')
        .from('portal_access')
        .select('department_id')
        .ilike('email', trimmed)
        .is('removed_at', null)
        .not('forms_role', 'is', null)
        .maybeSingle();
      const deptId = (wide as { department_id?: string | null } | null)?.department_id;
      if (deptId) {
        const { data: dept } = await getDbSupabase('forms')
          .from('departments')
          .select('slug')
          .eq('id', deptId)
          .maybeSingle();
        const slug = (dept as { slug?: string } | null)?.slug?.trim();
        if (slug) return slug;
      }
    }
  } catch {
    /* fall through */
  }

  return null;
}
